import React, { Component } from "react"
import Helmet from "react-helmet"
import styled, { css } from "styled-components"
import {
  TERTIARY_TEXT,
  SECONDARY_TEXT,
  PRIMARY_TEXT,
  SMALLCHAT_SHADOW,
  SMALLCHAT_PURPLE
} from "../components/consts/colors"
import FeatureHeaderBackground from "../assets/pngs/FeatureHeaderBackground.png"
import sideBySide from "../assets/svgs/sideBySide.svg"
import bkgAM from "../assets/pngs/bkg-am.png"
import autoMessage from "../assets/svgs/autoMessage.svg"
import bkgCustomize from "../assets/pngs/bkg-customize.png"
import Customize from "../assets/svgs/customize.svg"
import SectionThreeBackground from "../assets/pngs/SectionThreeBackground.png"

import FeatureGrid from "../components/FeatureGrid"
import AddtoSlack from "../components/AddtoSlack"
import ArticleCard from "../components/ArticleCard"
import FeatureCard from "../components/FeatureCard"
import SectionBackground from "../components/SectionBackground"
import SectionContainer from "../components/SectionContainer"
import SectionIllustration from "../components/SectionIllustration"
import SectionBlockContent from "../components/SectionBlockContent"
import withScreensize from "../components/withScreensize"
import SectionCallout from "../components/SectionCallout"
import SectionCalloutBackground from "../assets/svgs/SectionCalloutBackground.svg"

const features = [
  {
    icon: "clock",
    name: "Offline Scheduling",
    description:
      "Let your customers know when your team is around and available to help."
  },
  {
    icon: "autoMessages",
    name: "Auto Messaging",
    description:
      "Get the conversation started! Actively engage visitors with custom trigger messages."
  },
  {
    icon: "profile",
    name: "Know your Users",
    description:
      "Use the Smallchat API to add data and know more about the visitors you're chatting with."
  },
  {
    icon: "unlimited",
    name: "Unlimited Chats",
    description:
      "Don't worry about your bill going up if your customers are chatty. Unlimited messages on every tier."
  },
  {
    icon: "analytics",
    name: "Custom Analytics",
    description: "Track the number of conversations you're having each month."
  },
  {
    icon: "channels",
    name: "Multiple Channels",
    description:
      "Manage multiple sites from the same Slack team by creating a channel for each site."
  },
  {
    icon: "customize",
    name: "Fully Customizable",
    description: "Style your widget to match your unique brand."
  },
  {
    icon: "edit",
    name: "Edit Sent Messages",
    description:
      "No need to fret over typos. Edit or delete messages after they've been sent."
  },
  {
    icon: "website",
    name: "Use Across Multiple Sites",
    description:
      "Use Smallchat on multiple sites from a single Slack team, or add multiple Slack teams to a single Smallchat subscription."
  },
  {
    icon: "slack",
    name: "Slack + Smallchat",
    description:
      "Smallchat integrates seamlessly with Slack. Conversations create a new thread in your support channel."
  },
  {
    icon: "world",
    name: "Support Multiple Languages",
    description:
      "Create multiple widgets for different language versions of your site."
  },
  {
    icon: "phone",
    name: "Chat On the Go",
    description:
      "Chat from Slack's native mobile app, and get notifications when a customer starts a conversation."
  }
]

const FeatureGridHeader = styled.h2`
  z-index: 2;
  max-width: 600px;
  text-align: center;
  padding: 0 50px;
  h2 {
    display: inline-block;
  }

  @media screen and (max-width: 768px) {
    margin-top: 80px;
    padding: 0 20px;
  }
`

const FeatureWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
`

function Feature({ screensize }) {
  const isMobile = screensize === "phone"
  const isLargeMobile = screensize === "l-phone"
  const isTablet = screensize === "tablet"
  const isSmall = screensize === "small"

  return (
    <div>
      <Helmet title="Smallchat Features" />
      <FeatureWrapper>
        <SectionContainer
          height={isMobile ? "90vh" : isTablet ? "80vh" : "70vh"}>
          <SectionBlockContent
            headline="Realtime interactions."
            copy="Smallchat lets you engage visitors as they're browsing. People don’t want to wait and put a potential purchase on hold while they wait for an email response. Real-time chat allows you to answer visitor questions in real-time, converting more visitors to customers."
          />
          <SectionIllustration
            position={isMobile ? "-18%" : isTablet ? "10%" : "0%"}
            transform={
              isMobile ? "scale(.55)" : isTablet ? "scale(.85)" : "scale(.9)"
            }
            name={sideBySide}
          />
          <SectionBackground
            background={FeatureHeaderBackground}
            backgroundHeight={isMobile ? "80%" : isTablet ? "110%" : "140%"}
            backgroundWidth={isMobile ? "140%" : isTablet ? "150%" : "160%"}
            backgroundScale={isMobile ? "1.1" : isTablet ? "1" : ".9"}
          />
        </SectionContainer>
        <SectionContainer
          height={isMobile ? "45vh" : isTablet ? "1.6" : "50vh"}>
          <FeatureCard
            name="unlimited"
            title="Get Unlimited Operators, Conversations, and Messages. For Free."
          />
        </SectionContainer>
        <SectionContainer
          reversed
          height={isMobile ? "50vh" : isTablet ? "60vh" : "70vh"}>
          <SectionBlockContent
            bigIllustration
            headline="Automate Engagement"
            copy="Get the conversation started! Actively engage visitors with custom trigger messages."
            // link="/"
            // linkText="Learn More about Auto Messages"
          />
          <SectionIllustration
            bigIllustration
            position={
              isMobile ? "-25%" : isTablet ? "0%" : isSmall ? "-25%" : "-5%"
            }
            transform={
              isMobile ? "scale(.45)" : isTablet ? "scale(.75)" : "scale(.9)"
            }
            name={autoMessage}
          />
          <SectionBackground
            background={bkgAM}
            backgroundHeight="100%"
            backgroundWidth="120%"
            backgroundRight={isMobile ? "0%" : isTablet ? "0%" : "40%"}
            backgroundScale={isMobile ? "1.1" : isTablet ? "1" : "1"}
          />
        </SectionContainer>
        <SectionContainer
          height={isMobile ? "100vh" : isTablet ? "90vh" : "90vh"}>
          <SectionBlockContent
            headline="Fully Customizable"
            copy="Customize your site's widget with quick & easy styling to seamlessly integrate Smallchat with your brand."
            protips="💡 All forms & fields are completely customizable to work in any language!"
            // link="/"
            // linkText="See it in Action!"
          />
          <SectionIllustration
            position={isMobile ? "-27%" : isTablet ? "0%" : "0%"}
            transform={
              isMobile ? "scale(.45)" : isTablet ? "scale(.7)" : "scale(.9)"
            }
            name={Customize}
          />
          <SectionBackground
            background={bkgCustomize}
            backgroundHeight={isMobile ? "90%" : isTablet ? "95%" : "100%"}
            backgroundWidth={isMobile ? "160%" : isTablet ? "120%" : "100%"}
            backgroundRight="-60%"
            backgroundScale={isMobile ? ".9" : isTablet ? "1" : "1.2"}
          />
        </SectionContainer>
        <SectionContainer
          height={isMobile ? "30vh" : isTablet ? "10vh" : "30vh"}>
          <FeatureGridHeader>
            All the Features you need, whatever your business.
          </FeatureGridHeader>
        </SectionContainer>
        <SectionContainer
          height={
            isMobile ? (
              "200vh"
            ) : isLargeMobile ? (
              "180vh"
            ) : isTablet ? (
              "140vh"
            ) : (
              "90vh"
            )
          }>
          <FeatureGrid features={features} />
          <SectionBackground
            background={SectionThreeBackground}
            backgroundHeight="100%"
            backgroundWidth="100%"
            backgroundRight="40%"
            backgroundScale={isMobile ? ".8" : isTablet ? "1" : "1.2"}
          />
        </SectionContainer>
        <SectionCallout
          darkShadow
          background={SectionCalloutBackground}
          backgroundSize="cover"
          backgroundPosition="80% 80%"
          title="Start your conversations today."
        />
      </FeatureWrapper>
    </div>
  )
}

export default withScreensize(Feature)



// WEBPACK FOOTER //
// ./src/pages/features.js